import { useEffect } from 'react'
import { Routes, Route, Navigate, useSearchParams, useParams, useLocation } from 'react-router-dom'
import { useAtom } from 'jotai'
import { fetchAuthSession } from 'aws-amplify/auth'
import { userAtom, isLoadingAtom, User } from './state/auth'
import { workflowStageDefinitionsAtom, workflowStagesLoadingAtom } from './state/clients'
import { isClientUser } from './utils/roles'
import { salesStageDefinitionsAtom, salesStagesLoadingAtom } from './state/leads'
import { interviewStageDefinitionsAtom, interviewStagesLoadingAtom } from './state/interviews'
import {
  jobApplicationStageDefinitionsAtom,
  jobApplicationStagesLoadingAtom,
} from './state/jobOpportunities'
import {
  workflowStagesApi,
  salesStagesApi,
  interviewStagesApi,
  jobApplicationStagesApi,
  usersApi,
} from './services/api'
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import JobSearchClientIndex from './pages/JobSearchClientIndex'
import GlobalStaffNotifications from './components/GlobalStaffNotifications'
import Login from './pages/Login'
import SetPassword from './pages/auth/SetPassword'
import Dashboard from './pages/Dashboard'
import Leads from './pages/Leads'
import Clients from './pages/Clients'
import ClientDetail from './pages/ClientDetail'
import Tasks from './pages/Tasks'
import Callback from './pages/Callback'
import ClientPipeline from './pages/ClientPipeline'
import JobApplicationsTracker from './pages/JobApplicationsTracker'
import ClientSignals from './pages/ClientSignals'
import Networking from './pages/Networking'
import Users from './pages/Users'
import Messages from './pages/Messages'
import Emails from './pages/Emails'
import ProductCatalog from './pages/ProductCatalog'
import StageGates from './pages/StageGates'
import ManageChecklists from './pages/ManageChecklists'
import ManageOutreachTemplates from './pages/ManageOutreachTemplates'
import AIPrompts from './pages/AIPrompts'
import CompanyContext from './pages/CompanyContext'
import NorthStarTemplates from './pages/admin/NorthStarTemplates'
import NorthStarEditor from './pages/admin/NorthStarEditor'
import NorthStarPublic from './pages/NorthStarPublic'
import ResumeTemplates from './pages/ResumeTemplates'
import Settings from './pages/Settings'
import Profile from './pages/Profile'
import ProposalPage from './pages/ProposalPage'
import LegalDocumentPage from './pages/LegalDocumentPage'
import { LEGAL_DOCUMENTS } from './content/legal'
import Help from './pages/Help'
// Portal imports
import PortalLayout from './components/portal/PortalLayout'
import PortalDataProvider from './components/portal/PortalDataProvider'
import PortalUnavailable from './components/portal/PortalUnavailable'
import PortalDashboard from './pages/portal/PortalDashboard'
import PortalProfile from './pages/portal/PortalProfile'
import PortalNetworking from './pages/portal/PortalNetworking'
import PortalJobApplications from './pages/portal/PortalJobApplications'
import PortalSignals from './pages/portal/PortalSignals'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [user] = useAtom(userAtom)
  const [isLoading] = useAtom(isLoadingAtom)

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Redirect client users to the portal. See isClientUser for why this is not
  // just a role check, and why the portal guard below must use the same test.
  if (isClientUser(user)) {
    return <Navigate to="/portal" replace />
  }

  return <>{children}</>
}

function ProtectedPortalRoute({ children }: { children: React.ReactNode }) {
  const [user] = useAtom(userAtom)
  const [isLoading] = useAtom(isLoadingAtom)

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // The same test the staff guard uses, so the two can never disagree about
  // where a user belongs and bounce them between each other.
  if (!isClientUser(user)) {
    return <Navigate to="/" replace />
  }

  // Gate on clientId so every portal child can assume user.clientId is a
  // non-empty string at mount time. Fixes the race where portal pages'
  // `if (clientId) loadData()` guards would silently skip on initial login
  // if the JWT claim hadn't propagated yet, leaving the page in an
  // infinite loading state until manual refresh.
  if (!user.clientId) {
    return <LoadingSpinner />
  }

  return <>{children}</>
}

// Job Search lives at /clients/:id/opportunities and /clients/:id/networking,
// so the bare paths — kept for bookmarks and the sidebar — have to answer
// "which client".
//
// They used to answer it themselves and redirect, to the remembered client or
// the first market-ready one. That left no way to see the list and pick, and
// below sm no way to change client at all, because the navbar switcher is
// hidden at that width. They now render a searchable index instead.
//
// An explicit ?clientId= still redirects, for old bookmarks and any external
// link that still carries it. Nothing in the app builds that query for these
// paths any more — Client Health's View button navigates straight to the scoped
// URL — so this branch is compatibility, not a live code path.
//
// MWA-338 — job applications no longer asks. Opening it shows every application
// across the clients you are responsible for, and its own picker changes client
// at any width, so the index would only be a stop on the way to the same place.
// Networking still asks, because it has no all-clients view.
function JobSearchEntry({ page }: { page: 'job-applications' | 'networking' }) {
  const [searchParams] = useSearchParams()
  const requestedClientId = searchParams.get('clientId')

  if (requestedClientId) {
    const segment = page === 'job-applications' ? 'opportunities' : page
    return <Navigate to={`/clients/${requestedClientId}/${segment}`} replace />
  }

  if (page === 'job-applications') {
    return <JobApplicationsTracker />
  }

  return <JobSearchClientIndex key={page} page={page} />
}

// Opportunities was served at .../job-applications until it was renamed. The
// old paths redirect rather than 404 so bookmarks, and links already sent in
// emails and notifications, keep landing on the page. The query string carries
// over because /job-applications?clientId= is itself a legacy link format.
function LegacyJobApplicationsRedirect({ to }: { to: string }) {
  const { id } = useParams()
  const { search, hash } = useLocation()
  return <Navigate to={`${to.replace(':id', id ?? '')}${search}${hash}`} replace />
}

function App() {
  const [user, setUser] = useAtom(userAtom)
  const [, setIsLoading] = useAtom(isLoadingAtom)
  const [, setWorkflowStageDefinitions] = useAtom(workflowStageDefinitionsAtom)
  const [, setWorkflowStagesLoading] = useAtom(workflowStagesLoadingAtom)
  const [, setSalesStageDefinitions] = useAtom(salesStageDefinitionsAtom)
  const [, setSalesStagesLoading] = useAtom(salesStagesLoadingAtom)
  const [, setInterviewStageDefinitions] = useAtom(interviewStageDefinitionsAtom)
  const [, setInterviewStagesLoading] = useAtom(interviewStagesLoadingAtom)
  const [, setJobApplicationStageDefinitions] = useAtom(jobApplicationStageDefinitionsAtom)
  const [, setJobApplicationStagesLoading] = useAtom(jobApplicationStagesLoadingAtom)

  useEffect(() => {
    const checkSession = async () => {
      try {
        // forceRefresh: true will attempt to refresh expired tokens
        const session = await fetchAuthSession({ forceRefresh: true })
        const idToken = session.tokens?.idToken
        if (idToken) {
          const payload = idToken.payload
          const user: User = {
            email: (payload.email as string) || '',
            role: (payload['custom:role'] as User['role']) || 'sales',
            name: payload.name as string | undefined,
            clientId: payload['custom:clientId'] as string | undefined,
            isManager: payload['custom:isManager'] === 'true',
          }

          // Retry once if this is a client user but the JWT lacks clientId.
          // Cognito occasionally issues the first post-login JWT without
          // propagated custom attributes, which would otherwise leave the
          // portal stuck on an infinite loading spinner (the original
          // clientId race). A single forceRefresh grabs the fully populated
          // JWT on retry.
          if (user.role === 'client' && !user.clientId) {
            try {
              const retry = await fetchAuthSession({ forceRefresh: true })
              const retryPayload = retry.tokens?.idToken?.payload
              if (retryPayload) {
                user.clientId = retryPayload['custom:clientId'] as string | undefined
              }
            } catch {
              // Fall through — ProtectedPortalRoute shows a LoadingSpinner
              // until clientId arrives; if it never does, user can refresh
              // manually or the logout path clears state.
            }
          }

          // Also check the DB record for isManager in case Cognito attribute
          // hasn't propagated to the JWT yet
          if (user.role !== 'client') {
            try {
              const users = await usersApi.list()
              const dbUser = users.find((u) => u.email === user.email)
              if (dbUser?.isManager) {
                user.isManager = true
              }
            } catch {
              // Non-critical — fall back to JWT claim only
            }
          }
          setUser(user)
        }
      } catch (error) {
        // No valid session or refresh failed, user will be redirected to login
        console.log('No active session:', error)
      } finally {
        setIsLoading(false)
      }
    }

    checkSession()
  }, [setUser, setIsLoading])

  useEffect(() => {
    if (!user) return

    const loadWorkflowStages = async () => {
      try {
        setWorkflowStagesLoading(true)
        const stages = await workflowStagesApi.list()
        setWorkflowStageDefinitions(stages)
      } catch (error) {
        console.error('Failed to load workflow stages:', error)
        setWorkflowStageDefinitions([])
      } finally {
        setWorkflowStagesLoading(false)
      }
    }

    loadWorkflowStages()
  }, [user, setWorkflowStageDefinitions, setWorkflowStagesLoading])

  useEffect(() => {
    if (!user) return

    const loadSalesStages = async () => {
      try {
        setSalesStagesLoading(true)
        const stages = await salesStagesApi.list()
        setSalesStageDefinitions(stages)
      } catch (error) {
        console.error('Failed to load sales stages:', error)
        setSalesStageDefinitions([])
      } finally {
        setSalesStagesLoading(false)
      }
    }

    loadSalesStages()
  }, [user, setSalesStageDefinitions, setSalesStagesLoading])

  useEffect(() => {
    if (!user) return

    const loadInterviewStages = async () => {
      try {
        setInterviewStagesLoading(true)
        const stages = await interviewStagesApi.list()
        setInterviewStageDefinitions(stages)
      } catch (error) {
        console.error('Failed to load interview stages:', error)
        setInterviewStageDefinitions([])
      } finally {
        setInterviewStagesLoading(false)
      }
    }

    loadInterviewStages()
  }, [user, setInterviewStageDefinitions, setInterviewStagesLoading])

  useEffect(() => {
    if (!user) return

    const loadJobApplicationStages = async () => {
      try {
        setJobApplicationStagesLoading(true)
        const stages = await jobApplicationStagesApi.list()
        setJobApplicationStageDefinitions(stages)
      } catch (error) {
        console.error('Failed to load job application stages:', error)
        setJobApplicationStageDefinitions([])
      } finally {
        setJobApplicationStagesLoading(false)
      }
    }

    loadJobApplicationStages()
  }, [user, setJobApplicationStageDefinitions, setJobApplicationStagesLoading])

  return (
    <>
      {/* #199 — global staff-side toast for cross-page team awareness */}
      <GlobalStaffNotifications />
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Callback />} />
      <Route path="/logout" element={<Navigate to="/login" replace />} />
      <Route path="/auth/set-password" element={<SetPassword />} />

      {/* Public Proposal Page (no auth required) */}
      <Route path="/proposal/:token" element={<ProposalPage />} />

      {/* Public North Star microsite (no auth required) */}
      <Route path="/north-star/:slug" element={<NorthStarPublic />} />

      {/* Public legal documents (no auth required): prospective clients read
          these before they have an account. */}
      {LEGAL_DOCUMENTS.map((doc) => (
        <Route key={doc.path} path={doc.path} element={<LegalDocumentPage document={doc} />} />
      ))}

      {/* Client Portal Routes */}
      <Route
        path="/portal/*"
        element={
          <ProtectedPortalRoute>
            <PortalDataProvider>
              <PortalLayout>
                <Routes>
                  <Route path="/" element={<PortalDashboard />} />
                  <Route path="/profile" element={<PortalProfile />} />
                  <Route path="/networking" element={<PortalNetworking />} />
                  <Route path="/opportunities" element={<PortalJobApplications />} />
                  <Route path="/signals" element={<PortalSignals />} />
                  <Route path="/job-applications" element={<LegacyJobApplicationsRedirect to="/portal/opportunities" />} />
                  {/* A URL that never existed answers exactly as a gated page
                      does, so the portal cannot be probed for what exists. */}
                  <Route path="*" element={<PortalUnavailable />} />
                </Routes>
              </PortalLayout>
            </PortalDataProvider>
          </ProtectedPortalRoute>
        }
      />

      {/* Staff Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/client-pipeline" element={<ClientPipeline />} />
                <Route path="/opportunities" element={<JobSearchEntry page="job-applications" />} />
                <Route path="/job-applications" element={<LegacyJobApplicationsRedirect to="/opportunities" />} />
                <Route path="/networking" element={<JobSearchEntry page="networking" />} />
                <Route path="/clients/:id/opportunities" element={<JobApplicationsTracker />} />
                <Route path="/clients/:id/signals" element={<ClientSignals />} />
                <Route path="/clients/:id/job-applications" element={<LegacyJobApplicationsRedirect to="/clients/:id/opportunities" />} />
                <Route path="/clients/:id/networking" element={<Networking />} />
                <Route path="/leads" element={<Leads />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/emails" element={<Emails />} />
                <Route path="/users" element={<Users />} />
                <Route path="/products" element={<ProductCatalog />} />
                <Route path="/stage-gates" element={<StageGates />} />
                <Route path="/outreach-checklists" element={<ManageChecklists />} />
                <Route path="/outreach-templates" element={<ManageOutreachTemplates />} />
                <Route path="/ai-prompts" element={<AIPrompts />} />
                <Route path="/company-context" element={<CompanyContext />} />
                <Route path="/north-star-templates" element={<NorthStarTemplates />} />
                <Route path="/clients/:id/north-star" element={<NorthStarEditor />} />
                <Route path="/resume-templates" element={<ResumeTemplates />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/help" element={<Help />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
      </Routes>
    </>
  )
}

export default App
